import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { addModulePlacement } from "./addModulePlacement";
import type { DockviewApi } from "dockview-react";
import {
  MAX_WORKSPACES,
  activeWorkspace,
  addWorkspace,
  duplicateWorkspace,
  moveWorkspace,
  removeWorkspace,
  renameWorkspace,
  resetWorkspace,
  setActiveWorkspace,
  setWorkspaceLocked,
  firstRunDismissPatch,
  firstRunSetupPending,
  useSettings,
  type WorkspaceEntry,
} from "@helm/core";
import { settingsSync } from "../session";
import { FirstRunSetup } from "./FirstRunSetup";
import { usePopover } from "../components/Popover";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Icon } from "../components/Icon";
import { MODULES, TEMPLATES, WIDGET_TITLES, type ModuleInfo, type WidgetKind } from "./templates";
import { newWorkspaceId, useWorkspaces, type WorkspaceStore } from "./workspaceStore";
import { workspaceActions } from "./Workspace";
import { WorkspacePicker } from "./WorkspacePicker";

/**
 * How much the bar folds to fit the position strip: 0 every tab; 1 the tabs beyond the active one
 * fold into its "▾" menu; 2 also "+ Module" / "Lock" (words kept, never icon-only).
 */
type Fit = 0 | 1 | 2;
/** Room to spare before unfolding, so a P&L that grows a digit does not flip the bar back. */
const UNFOLD_SLACK = 24;

/**
 * Folds the bar to its room: measures the parts marked `data-fit-part` (their natural widths: they
 * do not shrink at levels 0 and 1) against the bar, folds one level while they overflow, and
 * unfolds when the bar has the width the level above needed. Resets to 0 when `reset` changes (a
 * workspace added, renamed…). Layout effects: every step settles before paint.
 */
function useFit(reset: string): { ref: React.RefObject<HTMLElement | null>; fit: Fit } {
  const ref = useRef<HTMLElement | null>(null);
  const [fit, setFit] = useState<Fit>(0);
  // The width the parts needed at levels 0 and 1, for the content they had then (`key`).
  const need = useRef<{ key: string; at: number[] }>({ key: reset, at: [] });
  const [lastReset, setLastReset] = useState(reset);
  if (lastReset !== reset) {
    setLastReset(reset);
    setFit(0);
  }
  useLayoutEffect(() => {
    const nav = ref.current;
    if (!nav) return;
    if (need.current.key !== reset) need.current = { key: reset, at: [] };
    const at = need.current.at;
    const measure = (): void => {
      const parts = [...nav.querySelectorAll<HTMLElement>(":scope > [data-fit-part]")];
      const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
      // The parts, and a gap after each (the last one's is the spacer's).
      const content = parts.reduce((s, el) => s + el.getBoundingClientRect().width + gap, 0);
      const room = nav.clientWidth;
      if (fit < 2 && content > room + 0.5) {
        at[fit] = content;
        setFit((fit + 1) as Fit);
      } else if (fit > 0 && room >= (at[fit - 1] ?? Infinity) + UNFOLD_SLACK) {
        setFit((fit - 1) as Fit);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    for (const el of nav.querySelectorAll(":scope > [data-fit-part]")) ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [fit, reset]);
  return { ref, fit };
}

/**
 * Workspace tabs (owner 2026-09-27; SPEC §17.3): one tab per named workspace — click to switch
 * (Ctrl+1…9), double-click to rename, drag to reorder, ⋯ for duplicate / reset to template / lock /
 * delete — then "+" for a new workspace, "Add module" and the layout lock.
 *
 * It sits in the position strip (owner 2026-10-04: no separate workspace row), between Working and
 * Flatten. Short of room, the tabs beyond the active one fold into a "Workspace 1 ▾" menu (switch,
 * rename, new, delete), then "+ Add module" / "Lock layout" shorten to "+ Module" / "Lock".
 */
export function WorkspaceBar({
  store,
  dock,
}: {
  store: WorkspaceStore;
  dock: () => DockviewApi | null;
}): React.JSX.Element {
  const { doc } = useWorkspaces(store);
  const active = activeWorkspace(doc);
  const toolbarOn = useSettings((s) => s.settings.top_toolbar);
  const style = useSettings((s) => s.settings.visual_style);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WorkspaceEntry | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [invitation, setInvitation] = useState(false);
  // First-run setup: only a new user's settings document has it pending (NEW_USER_SETTINGS).
  const setupPending = useSettings((s) => s.loaded && firstRunSetupPending(s.settings));
  const [setupOpen, setSetupOpen] = useState(false);
  useEffect(() => {
    let current = true;
    void store.start().then(() => {
      if (current) setInvitation(store.firstRunEligible);
    });
    return () => {
      current = false;
    };
  }, [store]);
  // Anything that changes the bar's widths re-measures from every tab shown.
  const { ref, fit } = useFit(
    [
      style,
      String(toolbarOn),
      active.id,
      renaming ?? "",
      String(setupPending),
      String(invitation),
      ...doc.workspaces.map((w) => `${w.id}:${w.name}:${w.locked ? "1" : "0"}`),
    ].join("|"),
  );
  const canAdd = doc.workspaces.length < MAX_WORKSPACES;
  const canDelete = doc.workspaces.length > 1;
  const openPicker = (): void => {
    setPickerOpen(true);
    setInvitation(false);
  };
  // "+" stays when the tabs fold: the picker hands focus back to it when it closes.
  const newButton = (
    <button
      type="button"
      className="icon-btn icon-btn-xs ws-add"
      aria-label="New workspace"
      title={canAdd ? "New workspace" : "Workspace limit reached"}
      disabled={!canAdd}
      onClick={openPicker}
    >
      <Icon name="plus" size={12} />
    </button>
  );
  const menuFor = (w: WorkspaceEntry): React.JSX.Element => (
    <WorkspaceMenu
      entry={w}
      canDelete={canDelete}
      canAdd={canAdd}
      onRename={() => {
        setRenaming(w.id);
      }}
      onDuplicate={() => {
        store.update((d) => duplicateWorkspace(d, w.id, newWorkspaceId()));
      }}
      onReset={(template) => {
        store.update((d) => resetWorkspace(d, w.id, template), { rebuild: true });
      }}
      onToggleLock={() => {
        store.update((d) => setWorkspaceLocked(d, w.id, !w.locked));
      }}
      onDelete={() => {
        setConfirmDelete(w);
      }}
    />
  );
  const renameInput = (w: WorkspaceEntry): React.JSX.Element => (
    <RenameInput
      initial={w.name}
      onDone={(name) => {
        if (name !== null) store.update((d) => renameWorkspace(d, w.id, name));
        setRenaming(null);
      }}
    />
  );

  return (
    <nav ref={ref} className="ws-bar" aria-label="Workspaces" data-fit={fit}>
      {fit > 0 ? (
        <div className="ws-tabs ws-tabs-folded" data-fit-part="">
          <div className="ws-tab active ws-switcher">
            {renaming === active.id ? (
              renameInput(active)
            ) : (
              <WorkspaceSwitcher
                workspaces={doc.workspaces}
                active={active}
                canAdd={canAdd}
                canDelete={canDelete}
                onSwitch={(id) => {
                  store.update((d) => setActiveWorkspace(d, id));
                }}
                onRename={() => {
                  setRenaming(active.id);
                }}
                onNew={openPicker}
                onDelete={() => {
                  setConfirmDelete(active);
                }}
              />
            )}
            {renaming !== active.id && menuFor(active)}
          </div>
          {newButton}
          <FirstRunOffer
            setupPending={setupPending}
            invitation={invitation && store.firstRunEligible}
            onSetup={() => {
              setSetupOpen(true);
            }}
            onChoose={openPicker}
            onLater={() => {
              setInvitation(false);
            }}
          />
        </div>
      ) : (
        <div className="ws-tabs" role="tablist" aria-label="Workspaces" data-fit-part="">
          {doc.workspaces.map((w, i) => (
            <div
              key={w.id}
              className={[
                "ws-tab",
                w.id === active.id ? "active" : "",
                dragId === w.id ? "dragging" : "",
                dropAt === i && dragId !== null && dragId !== w.id ? "drop-before" : "",
              ].join(" ")}
              draggable={renaming !== w.id}
              onDragStart={(e) => {
                setDragId(w.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("application/x-helm-workspace", w.id);
              }}
              onDragOver={(e) => {
                if (dragId === null) return;
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setDropAt(e.clientX < r.left + r.width / 2 ? i : i + 1);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId !== null && dropAt !== null) {
                  const from = doc.workspaces.findIndex((x) => x.id === dragId);
                  store.update((d) => moveWorkspace(d, dragId, dropAt > from ? dropAt - 1 : dropAt));
                }
                setDragId(null);
                setDropAt(null);
              }}
              onDragEnd={() => {
                setDragId(null);
                setDropAt(null);
              }}
            >
              {renaming === w.id ? (
                renameInput(w)
              ) : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={w.id === active.id}
                  className="ws-tab-btn"
                  title={`${w.name}${i < 9 ? ` · Ctrl+${String(i + 1)}` : ""} · double-click to rename, drag to reorder`}
                  onClick={() => {
                    store.update((d) => setActiveWorkspace(d, w.id));
                  }}
                  onDoubleClick={() => {
                    setRenaming(w.id);
                  }}
                >
                  {w.locked && <Icon name="lock" size={11} className="ws-lock-icon" />}
                  <span className="ws-tab-name">{w.name}</span>
                </button>
              )}
              {w.id === active.id && renaming !== w.id && menuFor(w)}
            </div>
          ))}
          {newButton}
          <FirstRunOffer
            setupPending={setupPending}
            invitation={invitation && store.firstRunEligible}
            onSetup={() => {
              setSetupOpen(true);
            }}
            onChoose={openPicker}
            onLater={() => {
              setInvitation(false);
            }}
          />
        </div>
      )}
      <span className="spacer" />
      {/* With the window-wide toolbar on, "+" and the lock live there (no duplicate controls). */}
      {!toolbarOn && <AddModuleButton dock={dock} short={fit === 2} fitPart />}
      {!toolbarOn && (
        <button
          type="button"
          className={active.locked ? "btn btn-sm ws-lock on" : "btn btn-sm ws-lock"}
          aria-pressed={active.locked}
          aria-label={fit === 2 ? (active.locked ? "Layout locked" : "Lock layout") : undefined}
          data-fit-part=""
          title={
            active.locked
              ? "Layout locked: modules can't be dragged, resized or closed. Click to unlock."
              : "Lock layout (prevents accidental drags, resizes and closes)"
          }
          onClick={() => {
            store.update((d) => setWorkspaceLocked(d, active.id, !active.locked));
          }}
        >
          <Icon name={active.locked ? "lock" : "unlock"} size={12} />
          {fit === 2 ? (active.locked ? "Locked" : "Lock") : active.locked ? "Layout locked" : "Lock layout"}
        </button>
      )}
      {pickerOpen && (
        <WorkspacePicker
          disabled={doc.workspaces.length >= MAX_WORKSPACES}
          onClose={() => {
            setPickerOpen(false);
          }}
          onCreate={(template, name) => {
            store.update((d) => addWorkspace(d, { id: newWorkspaceId(), template, name }));
          }}
        />
      )}
      {setupOpen && setupPending && (
        <FirstRunSetup
          activeTemplate={active.template}
          canAddWorkspace={doc.workspaces.length < MAX_WORKSPACES}
          onAddWorkspace={(template, name) => {
            store.update((d) => addWorkspace(d, { id: newWorkspaceId(), template, name }));
          }}
          onClose={() => {
            setSetupOpen(false);
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title={`Delete workspace “${confirmDelete.name}”?`}
          confirmLabel="Delete workspace"
          onCancel={() => {
            setConfirmDelete(null);
          }}
          onConfirm={() => {
            store.update((d) => removeWorkspace(d, confirmDelete.id));
            setConfirmDelete(null);
          }}
        >
          Its layout is removed on every device. Modules in other workspaces, link groups and account data are
          not affected.
        </ConfirmDialog>
      )}
    </nav>
  );
}

/** First run: "Set up your screen" / "Skip setup", else the "Choose a layout" invitation. */
function FirstRunOffer({
  setupPending,
  invitation,
  onSetup,
  onChoose,
  onLater,
}: {
  setupPending: boolean;
  invitation: boolean;
  onSetup: () => void;
  onChoose: () => void;
  onLater: () => void;
}): React.JSX.Element | null {
  if (setupPending)
    return (
      <span className="workspace-first-run">
        <button type="button" className="btn btn-sm btn-primary" onClick={onSetup}>
          Set up your screen
        </button>
        <button
          type="button"
          className="btn btn-sm"
          title="Keep the current layout and appearance; this setup is not offered again"
          onClick={() => {
            void settingsSync.update(firstRunDismissPatch());
          }}
        >
          Skip setup
        </button>
      </span>
    );
  if (!invitation) return null;
  return (
    <span className="workspace-first-run">
      <button type="button" className="btn btn-sm" onClick={onChoose}>
        Choose a layout
      </button>
      <button type="button" className="btn btn-sm" aria-label="Dismiss layout invitation" onClick={onLater}>
        Later
      </button>
    </span>
  );
}

/**
 * The folded tabs: "Workspace 1 ▾" opens every workspace (click to switch, Ctrl+1…9 as on the tabs),
 * then Rename, New workspace and Delete; the ⋯ and "+" beside it stay as on the tab.
 */
function WorkspaceSwitcher({
  workspaces,
  active,
  canAdd,
  canDelete,
  onSwitch,
  onRename,
  onNew,
  onDelete,
}: {
  workspaces: readonly WorkspaceEntry[];
  active: WorkspaceEntry;
  canAdd: boolean;
  canDelete: boolean;
  onSwitch: (id: string) => void;
  onRename: () => void;
  onNew: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  const pop = usePopover();
  const run = (f: () => void) => () => {
    pop.setOpen(false);
    f();
  };
  return (
    <div className="popover-anchor ws-switcher-anchor">
      <button
        type="button"
        className="ws-tab-btn ws-switcher-btn"
        aria-haspopup="menu"
        aria-label={`Workspace “${active.name}”: switch workspace`}
        title={`${active.name} · ${String(workspaces.length)} workspace${workspaces.length === 1 ? "" : "s"}: switch, rename, new, delete`}
        {...pop.triggerProps}
      >
        {active.locked && <Icon name="lock" size={11} className="ws-lock-icon" />}
        <span className="ws-tab-name">{active.name}</span>
        <Icon name="chevronDown" size={11} className="ws-switcher-caret" />
      </button>
      {pop.open && (
        <div
          className="popover menu ws-menu ws-switcher-menu"
          role="menu"
          aria-label="Workspaces"
          {...pop.panelProps}
        >
          <div className="menu-label">Workspaces</div>
          {workspaces.map((w, i) => (
            <button
              key={w.id}
              type="button"
              role="menuitemradio"
              aria-checked={w.id === active.id}
              className="menu-item"
              onClick={run(() => {
                onSwitch(w.id);
              })}
            >
              <Icon name={w.locked ? "lock" : "layout"} size={12} />
              <span className="ws-switcher-name">{w.name}</span>
              {w.id === active.id && <span className="muted"> · current</span>}
              {i < 9 && <span className="muted ws-switcher-key">Ctrl+{String(i + 1)}</span>}
            </button>
          ))}
          <div className="menu-sep" role="separator" />
          <button type="button" role="menuitem" className="menu-item" onClick={run(onRename)}>
            <Icon name="pencil" size={12} /> Rename
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            disabled={!canAdd}
            title={canAdd ? undefined : "Workspace limit reached"}
            onClick={run(onNew)}
          >
            <Icon name="plus" size={12} /> New workspace…
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item menu-danger"
            disabled={!canDelete}
            title={canDelete ? undefined : "The last workspace can't be deleted"}
            onClick={run(onDelete)}
          >
            <Icon name="trash" size={12} /> Delete…
          </button>
        </div>
      )}
    </div>
  );
}

function RenameInput({
  initial,
  onDone,
}: {
  initial: string;
  onDone: (name: string | null) => void;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement | null>(null);
  const done = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (name: string | null): void => {
    if (done.current) return;
    done.current = true;
    onDone(name);
  };
  return (
    <input
      ref={ref}
      className="input input-sm ws-rename"
      defaultValue={initial}
      aria-label="Workspace name"
      maxLength={40}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(e.currentTarget.value);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={(e) => {
        finish(e.currentTarget.value);
      }}
    />
  );
}

function WorkspaceMenu({
  entry,
  canDelete,
  canAdd,
  onRename,
  onDuplicate,
  onReset,
  onToggleLock,
  onDelete,
}: {
  entry: WorkspaceEntry;
  canDelete: boolean;
  canAdd: boolean;
  onRename: () => void;
  onDuplicate: () => void;
  onReset: (template: string) => void;
  onToggleLock: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  const pop = usePopover();
  const run = (f: () => void) => () => {
    pop.setOpen(false);
    f();
  };
  const current = TEMPLATES.find((t) => t.id === entry.template);
  return (
    <div className="popover-anchor">
      <button
        type="button"
        className="icon-btn icon-btn-xs ws-more"
        aria-label={`Workspace “${entry.name}” options`}
        aria-haspopup="menu"
        {...pop.triggerProps}
      >
        <Icon name="more" size={12} />
      </button>
      {pop.open && (
        <div className="popover menu ws-menu" role="menu" aria-label="Workspace" {...pop.panelProps}>
          <button type="button" role="menuitem" className="menu-item" onClick={run(onRename)}>
            <Icon name="pencil" size={12} /> Rename
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            disabled={!canAdd}
            onClick={run(onDuplicate)}
          >
            <Icon name="copy" size={12} /> Duplicate
          </button>
          <button type="button" role="menuitem" className="menu-item" onClick={run(onToggleLock)}>
            <Icon name={entry.locked ? "unlock" : "lock"} size={12} />{" "}
            {entry.locked ? "Unlock layout" : "Lock layout"}
          </button>
          <div className="menu-sep" role="separator" />
          <div className="menu-label">Reset to template</div>
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              className="menu-item"
              title={`Replace this workspace's layout with the ${t.name} template`}
              onClick={run(() => {
                onReset(t.id);
              })}
            >
              <Icon name="layout" size={12} /> {t.name}
              {t.id === current?.id && <span className="muted"> · current</span>}
            </button>
          ))}
          <div className="menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="menu-item menu-danger"
            disabled={!canDelete}
            title={canDelete ? undefined : "The last workspace can't be deleted"}
            onClick={run(onDelete)}
          >
            <Icon name="trash" size={12} /> Delete…
          </button>
        </div>
      )}
    </div>
  );
}

const GROUPS: readonly ModuleInfo["group"][] = ["Trading", "Market data", "Account", "Tools"];

/** "+ Add module" (Tradovate's "+ Add Modules"): every module type, grouped, with icons. */
export function AddModuleButton({
  dock,
  compact = false,
  short = false,
  fitPart = false,
}: {
  dock: () => DockviewApi | null;
  /** Icon-only "+" (the window-wide top toolbar). */
  compact?: boolean;
  /** "+ Module" (the position strip short of room); the accessible name stays "Add module". */
  short?: boolean;
  /** Measured by the workspace bar's fit (WorkspaceBar `useFit`). */
  fitPart?: boolean;
}): React.JSX.Element {
  const pop = usePopover();
  const [position, setPosition] = useState({ left: 12, top: 12 });
  useLayoutEffect(() => {
    if (!pop.open) return;
    const trigger = pop.triggerProps.ref.current;
    const panel = pop.panelProps.ref.current;
    const win = trigger?.ownerDocument.defaultView;
    if (!trigger || !panel || !win) return;
    const place = (): void => {
      setPosition(
        addModulePlacement(trigger.getBoundingClientRect(), panel.getBoundingClientRect(), {
          width: win.innerWidth,
          height: win.innerHeight,
        }),
      );
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(panel);
    win.addEventListener("resize", place);
    win.addEventListener("scroll", place, true);
    return () => {
      observer.disconnect();
      win.removeEventListener("resize", place);
      win.removeEventListener("scroll", place, true);
    };
  }, [pop.open, pop.triggerProps.ref, pop.panelProps.ref]);
  const add = (kind: WidgetKind): void => {
    const d = dock();
    if (d) workspaceActions.addModule(d, kind);
    pop.setOpen(false);
  };
  return (
    <div className="popover-anchor" data-fit-part={fitPart ? "" : undefined}>
      {compact ? (
        <button
          type="button"
          className="toptool-btn toptool-icon"
          aria-haspopup="menu"
          aria-label="Add module"
          title="Add module"
          {...pop.triggerProps}
        >
          <Icon name="plus" size={13} />
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-sm add-module-btn"
          aria-haspopup="menu"
          aria-label={short ? "Add module" : undefined}
          title={short ? "Add module" : undefined}
          {...pop.triggerProps}
        >
          <Icon name="plus" size={12} /> {short ? "Module" : "Add module"}
        </button>
      )}
      {pop.open &&
        createPortal(
          <div
            className="popover popover-right add-module-menu"
            role="menu"
            aria-label="Add module"
            {...pop.panelProps}
            style={{
              position: "fixed",
              boxSizing: "border-box",
              right: "auto",
              maxHeight: "calc(100vh - 24px)",
              ...position,
              overflowY: "auto",
            }}
          >
            {GROUPS.map((g) => (
              <div key={g} className="add-module-group">
                <div className="menu-label">{g}</div>
                <div className="add-module-grid">
                  {MODULES.filter((m) => m.group === g).map((m) => (
                    <button
                      key={m.kind}
                      type="button"
                      role="menuitem"
                      className="add-module-item"
                      title={m.later ? `${m.hint} — arrives in a later phase` : m.hint}
                      onClick={() => {
                        add(m.kind);
                      }}
                    >
                      <Icon name={m.icon} size={14} />
                      <span className="add-module-name">{WIDGET_TITLES[m.kind]}</span>
                      {m.later && <span className="add-module-tag">later</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="muted add-module-foot">
              Drag a module’s tab to any edge to split it off, or onto another tab to stack it. The “⋯” in its
              header has its options and maximize, float or pop out; right-click a tab for more.
            </div>
          </div>,
          pop.triggerProps.ref.current?.ownerDocument.body ?? document.body,
        )}
    </div>
  );
}
